import { useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  addNotificationToChatBox,
  addNotificationToSidebar,
  removeNotificationFromSidebar,
  removeNotificationFromChatBox,
  clearAllNotifications,
} from "@store/notificationsSlice";
import { useDeleteNotificationMutation } from "@store/api/notifications";

const TOAST_VISIBLE_MS = 2000;
const POINTS_TOAST_WINDOW_MS = 8000;

// The toast is a single shared slot, so this bookkeeping is deliberately
// module level: it must be shared by every consumer of the hook.
let latestToastNonce = 0;
let pointsWindowClosesAt = 0;
let pointsPendingTotal = 0;
let pointsSummaryTimer = null;
let pointsSummaryTemplate = null;

const clearPointsWindow = () => {
  if (pointsSummaryTimer) {
    clearTimeout(pointsSummaryTimer);
  }
  pointsSummaryTimer = null;
  pointsPendingTotal = 0;
  pointsSummaryTemplate = null;
};

// Called when the notification subscription tears down, so points pending from
// a meeting cannot surface in the next one or after logout.
export const resetPointsThrottle = () => {
  clearPointsWindow();
  pointsWindowClosesAt = 0;
};

const useNotifications = () => {
  const dispatch = useDispatch();
  const notifications = useSelector((state) => state.notifications.sidebar);
  const chatNotification = useSelector((state) => state.notifications.chat);
  const [deleteNotification] = useDeleteNotificationMutation();

  // The chat banner middleware writes to the same slot with its own nonce, so
  // ownership is checked against the live slot rather than a local counter.
  const chatNotificationRef = useRef(chatNotification);
  chatNotificationRef.current = chatNotification;

  const showToast = useCallback(
    (notification) => {
      const nonce = `toast-${++latestToastNonce}`;
      dispatch(addNotificationToChatBox({ notification: { ...notification, nonce } }));

      setTimeout(() => {
        // A newer toast may have taken the slot; only clear our own.
        if (chatNotificationRef.current?.nonce === nonce) {
          dispatch(removeNotificationFromChatBox());
        }
      }, TOAST_VISIBLE_MS);
    },
    [dispatch]
  );

  const summaryOf = (template, total) => ({
    ...template,
    meta: { ...template.meta, eventType: "POINTS_SUMMARY", value: total },
  });

  const flushPointsSummary = useCallback(() => {
    const total = pointsPendingTotal;
    const template = pointsSummaryTemplate;
    clearPointsWindow();

    if (!total || !template) {
      return;
    }

    pointsWindowClosesAt = Date.now() + POINTS_TOAST_WINDOW_MS;
    showToast(summaryOf(template, total));
  }, [showToast]);

  const showPointsToast = useCallback(
    (data) => {
      const now = Date.now();
      const pointsInEvent = Number(data?.meta?.value) || 0;

      if (now >= pointsWindowClosesAt) {
        // Background tabs can defer the summary timer well past the window, so
        // fold anything still pending into this toast rather than letting it
        // arrive later out of context.
        const carried = pointsPendingTotal;
        const template = pointsSummaryTemplate;
        clearPointsWindow();

        pointsWindowClosesAt = now + POINTS_TOAST_WINDOW_MS;
        showToast(
          carried && template
            ? summaryOf(data, carried + pointsInEvent)
            : data
        );
        return;
      }

      pointsPendingTotal += pointsInEvent;
      pointsSummaryTemplate = data;

      if (!pointsSummaryTimer) {
        pointsSummaryTimer = setTimeout(
          flushPointsSummary,
          Math.max(pointsWindowClosesAt - now, 0)
        );
      }
    },
    [showToast, flushPointsSummary]
  );

  const handleAddNotification = useCallback(
    (data) => {
      dispatch(addNotificationToSidebar({ notification: data }));

      if (data?.type === "points") {
        showPointsToast(data);
        return;
      }

      showToast(data);
    },
    [dispatch, showToast, showPointsToast]
  );

  const handleRemoveNotification = useCallback((notificationId, type) => {
    if (type === "participant") {
      dispatch(removeNotificationFromSidebar({ notificationId }));
      return;
    }

    dispatch(removeNotificationFromSidebar({ notificationId }));
    deleteNotification({
      notificationId,
    });
  }, [dispatch, deleteNotification]);

  const handleRemoveAllNotifications = useCallback(() => {
    dispatch(clearAllNotifications());
    deleteNotification();
  }, [dispatch, deleteNotification]);

  return {
    notifications,
    chatNotification,
    handleAddNotification,
    handleRemoveNotification,
    handleRemoveAllNotifications,
  };
};

export default useNotifications;
